import {
  type CentAmount,
  getFirstAttributeValue,
  getTotalAppliedReductions,
  type Price,
  type Product,
  type Value,
} from '@scayle/storefront-nuxt'
import type { Order, OrderItem, OrderVariant } from '~/types/order'
import { useTracking } from '~/composables/useTracking'
import { useProductClickStorage } from '~/composables/useProductClickStorage'
import { useCurrentShop } from '#storefront/composables'
import {
  divideByHundred,
  formatPriceWithDecimals,
  getDeepestCategoryAttribute,
  getFirstCarrierKey,
  getShippingCost,
} from '~/utils'
import { getItemCategory4FromProduct } from '~/utils/tracking'
import type { OrderItemProduct } from '~/types/tracking'

/**
 * Retrieves unique items from an order based on their variant ID.
 * If there are two items with the same variant ID, first one will be retrieved.
 *
 * @param {Order} order - The order containing items.
 * @returns {OrderItem[]} - The unique items from the order.
 */
const getUniqueItemsFromOrder = (order: Order): OrderItem[] => {
  if (!order.items?.length) {
    return []
  }

  const uniqueItems = new Map<number | string, OrderItem>()

  for (const item of order.items) {
    const variantId = item.variant.id
    if (!uniqueItems.has(variantId)) {
      uniqueItems.set(variantId, item)
    }
  }

  return Array.from(uniqueItems.values())
}

/**
 * Gets the quantity of a specific item variant from an order.
 *
 * @param {Order} order - The order containing items.
 * @param {number} variantId - The ID of the item variant.
 * @returns {number | undefined} - The quantity of the specified item variant in the order.
 */
const getItemQuantityFromOrder = (
  order: Order,
  variantId: number,
): number | undefined => {
  return order.items?.reduce((count, { variant }) => {
    return count + (variant.id === variantId ? 1 : 0)
  }, 0)
}

/**
 * Banker's rounding (round half to even)
 * @param {number} value
 * @returns {number}
 */
const bankersRound = (value: number) => {
  const floor = Math.floor(value)
  const diff = value - floor

  if (diff > 0.5) {
    return floor + 1
  }
  if (diff < 0.5) {
    return floor
  }

  // Exactly .5 → round to even
  return floor % 2 === 0 ? floor : floor + 1
}

/**
 * Calculate net and VAT from gross amount in cents
 * @param {number} grossCents - total gross amount in cents (e.g. 1598 for €15.98)
 * @param {number} vatRate - VAT rate as percentage (e.g. 19 for 19%)
 * @returns {{ netCents: number, vatCents: number }}
 */
const calculateVat = (grossCents: number, vatRate: number) => {
  const divisor = 1 + vatRate / 100

  const rawNet = grossCents / divisor
  const netCents = bankersRound(rawNet)
  const vatCents = grossCents - netCents

  return { netCents, vatCents }
}

const getOriginalPrice = (price: Price): CentAmount =>
  price.appliedReductions?.reduce(
    (previousPrice, appliedReduction) =>
      (previousPrice + appliedReduction.amount.absoluteWithTax) as CentAmount,
    price.withTax,
  )

const computePrices = (price?: {
  withTax: number
  withoutTax: number
  appliedReductions?: Array<{
    amount?: { relative?: number; absoluteWithTax?: number }
    category?: string
  }>
}) => {
  if (!price) {
    return {
      priceFormatted: 0.0,
      priceNet: 0,
      originalPrice: 0,
      reduction: undefined as
        | {
            typeWws: 'sale' | 'regular'
            percent?: number
            absolute?: number
            category?: string
          }
        | undefined,
    }
  }

  const applied = price.appliedReductions?.[0]
  const original = divideByHundred(getOriginalPrice(price))

  return {
    priceFormatted: formatPriceWithDecimals(price.withTax),
    priceNet: divideByHundred(price.withoutTax),
    originalPrice: original,
    reduction: applied
      ? {
          typeWws: 'sale' as const,
          percent: applied?.amount?.relative,
          absolute: applied?.amount?.absoluteWithTax
            ? divideByHundred(applied?.amount?.absoluteWithTax)
            : 0.0,
          category: applied?.category,
        }
      : { typeWws: 'regular' as const },
  }
}

const getItems = (
  orderData: Order,
  getProductClick: (productId: number) => any,
) => {
  const defaultEmptyValue = ''
  return getUniqueItemsFromOrder(orderData)?.map(
    (orderItem, _index: number) => {
      const product = orderItem.product as unknown as Product & OrderItemProduct
      const variant = orderItem.variant as unknown as OrderVariant
      const price = orderItem.price as unknown as Price
      const { reduction } = computePrices(price)
      const brandValue = product.attributes?.brand?.values as Value
      const deepestCategory = getDeepestCategoryAttribute(product)

      // Get stored product click data from localStorage
      const storedProductClick = getProductClick(product.id)

      // Check for Snipes exclusivity
      const isExclusive =
        getFirstAttributeValue(product.attributes, 'isExclusive')?.value ===
        'yes'
      const snipesExclusivity = isExclusive ? 'Snipes Exklusiv' : undefined

      const originalPriceValue =
        price.appliedReductions?.length > 0
          ? divideByHundred(
              getTotalAppliedReductions(price)?.absoluteWithTax +
                (price?.withTax ?? 0),
            )
          : divideByHundred(price?.withTax)
      return {
        item_id: product.id,
        item_name: product.name,
        item_price_net: price?.withoutTax
          ? divideByHundred(price.withoutTax)
          : 0,
        price: price?.withTax ? formatPriceWithDecimals(price.withTax) : 0.0,
        item_price_type_wws: reduction?.typeWws ?? 'regular',
        item_brand: brandValue.label || defaultEmptyValue,
        item_brand_code: brandValue.id || defaultEmptyValue,
        item_manufacturer_id:
          getFirstAttributeValue(
            product.attributes,
            'manufacturerCode',
          )?.value?.toString() || defaultEmptyValue,
        item_product_code:
          getFirstAttributeValue(product.attributes, 'productCode')?.label ||
          defaultEmptyValue,
        item_variant_id: variant.id,
        ...(variant.id &&
          getFirstAttributeValue(variant.attributes, 'size')?.label && {
            item_variant: getFirstAttributeValue(variant.attributes, 'size')
              ?.label,
          }),
        quantity: getItemQuantityFromOrder(orderData, variant.id),
        item_list_name: storedProductClick?.item_list_name || defaultEmptyValue,
        item_list_id: storedProductClick?.item_list_id || defaultEmptyValue,
        item_category:
          deepestCategory?.label.split('|').join(' > ') || defaultEmptyValue,
        item_category2:
          deepestCategory?.label.split('|').pop() || defaultEmptyValue,
        item_category3: deepestCategory?.value || defaultEmptyValue,
        item_category4: getItemCategory4FromProduct(product),
        item_price_original: originalPriceValue,
        item_discount_percentage: reduction?.percent,
        item_discount: reduction?.absolute,
        item_discount_type: reduction?.category,
        item_concept_exclusivity: snipesExclusivity,
        // Calculate percentage of available sizes vs total sizes
        item_availability_share: (() => {
          const variants = (product as any).variants
          if (!variants || !Array.isArray(variants) || variants.length === 0) {
            return undefined
          }

          // Check if variants have stock data loaded - if not, return undefined
          const hasStockData = variants.some((v: any) => v?.stock !== undefined)
          if (!hasStockData) {
            return undefined
          }

          const totalVariants = variants.length
          const availableVariants = variants.filter(
            (v: any) => v?.stock?.quantity && v.stock.quantity > 0,
          ).length

          if (totalVariants > 0) {
            const ratio = availableVariants / totalVariants
            return Math.round(ratio * 100) / 100 // Round to 2 decimal places
          }
          return undefined
        })(),
        // Check for online exclusivity
        item_exclusivity: (() => {
          const isOnlineExclusive =
            getFirstAttributeValue(product.attributes, 'onlineExclusiveFilter')
              ?.value === 'yes'
          return isOnlineExclusive ? 'Online Exklusiv' : undefined
        })(),
        // Get item_hy_code from affiliateRevenueModel
        item_hy_code:
          getFirstAttributeValue(product.attributes, 'affiliateRevenueModel')
            ?.value || defaultEmptyValue,
        // Get item_category5 from articleNoSearch (first 4 numbers)
        item_category5: (() => {
          const articleNoSearch = getFirstAttributeValue(
            product.attributes,
            'articleNoSearch',
          )?.value
          if (articleNoSearch) {
            return articleNoSearch.toString().substring(0, 4)
          }
          return defaultEmptyValue
        })(),
        // Get item_articlenumber with complete number using same logic
        item_articlenumber: (() => {
          const articleNoSearch = getFirstAttributeValue(
            product.attributes,
            'articleNoSearch',
          )?.value
          if (articleNoSearch) {
            return articleNoSearch.toString()
          }
          return defaultEmptyValue
        })(),
        // Get item_age from phasesOfLife attribute
        item_age: (() => {
          const phasesOfLife = getFirstAttributeValue(
            product.attributes,
            'phasesOfLife',
          )
          if (phasesOfLife?.label) {
            return phasesOfLife.label.toLowerCase()
          }
          return defaultEmptyValue
        })(),
        // Get item_gender from genders attribute
        item_gender: (() => {
          const genders = getFirstAttributeValue(product.attributes, 'genders')
          if (genders?.label) {
            return genders.label.toLowerCase()
          }
          return defaultEmptyValue
        })(),
        // Get item_sku from variant referenceKey only if variant is selected
        ...(variant.id &&
          variant.referenceKey && {
            item_sku: variant.referenceKey,
          }),
        // Get item_hr from isHotRelease
        item_hr: (() => {
          const isHotRelease = getFirstAttributeValue(
            product.attributes,
            'isHotRelease',
          )
          if (isHotRelease?.value) {
            return isHotRelease.value.toLowerCase() === 'yes'
          }
          return false
        })(),
      }
    },
  )
}

const usePurchaseEvents = (): {
  trackPurchaseEvent: (orderData: Order) => void
} => {
  const tracking = useTracking()
  const currencyCode = useCurrentShop().value!.currency
  const { getProductClick } = useProductClickStorage()

  function trackPurchaseEvent(orderData: Order) {
    const paymentType = orderData?.payment?.[0].key

    // Determine customer type
    const getCustomerType = (): 'guest' | 'existing' | 'new' => {
      if (orderData.customer?.status?.isGuestCustomer) {
        return 'guest'
      }
      return 'existing' // For now, treat all non-guest customers as existing
    }
    const coupon = orderData.vouchers?.[0]?.code ?? undefined // only one voucher can be applied for now

    const items = getItems(orderData, getProductClick)

    const shippingNetFee = getShippingCost(orderData.cost.appliedFees)

    // Calculate values for net_product_revenue calculation
    const orderValue = divideByHundred(orderData.cost.withTax)
    const shippingCost = shippingNetFee
      ? divideByHundred(shippingNetFee)
      : divideByHundred(orderData?.shipping?.deliveryCosts || 0)
    const netProductRevenue = Number(
      ((orderValue - shippingCost) / 1.19).toFixed(2),
    )

    // Take vat amount from OrderData
    const { vatCents } = calculateVat(orderData.cost.withTax, 19)

    const ecommerce = {
      currency: currencyCode,
      transaction_id: String(orderData.id),
      value: orderValue,
      coupon,
      shipping: shippingCost,
      net_product_revenue: netProductRevenue,
      payment_type: paymentType,
      customer_type: getCustomerType(),
      delivery_country:
        orderData.address?.shipping?.countryCode ??
        orderData.address?.forward?.countryCode ??
        'unknown',
      shipping_tier: getFirstCarrierKey(orderData) ?? 'unknown',
      taxrate: 0.19,
      tax: Number(divideByHundred(vatCents).toFixed(2)),
      coins_redeemed: 0,
      gift_cards_redeemed: 0,
      items,
    }

    tracking.push({
      event: 'purchase',
      ecommerce,
      category: 'ecommerce',
      action: 'purchase',
      label: 'order confirmation page',
      _clear: true,
    })
  }

  return {
    trackPurchaseEvent,
  }
}

export default usePurchaseEvents
